import { ChangeDetectorRef, Component, inject } from '@angular/core';
import type { SafeResourceUrl } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

import { PushPipe } from '@ngrx/component';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { Analytics, AnalyticsViewContext } from '@core/analytics-types';
import { HelpArticlesDirective } from '@core/help-articles-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  DialogData,
  DialogRef,
  RtStatefulComponent,
  PlatformThemeService,
  ButtonComponent,
  HeadingComponent,
  DialogBodyComponent,
} from '@core/user-interface-legacy';

import type { TutorialDialogModuleTranslations } from './translations';
import { TutorialTranslations } from './translations';
import { TutorialDialogNavComponent } from './tutorial-dialog-nav/tutorial-dialog-nav.component';
import type { TutorialStep } from './tutorial-dialog.types';

type State = {
  activeStepIndex: number;
  steps: TutorialStep[];
};

@Component({
  selector: 'rt-tutorial-dialog',
  templateUrl: './tutorial-dialog.component.html',
  styleUrls: ['./tutorial-dialog.component.less'],
  viewProviders: [provideAnalyticsViewContext(AnalyticsViewContext.TutorialDialog)],
  imports: [
    DialogBodyComponent,
    HeadingComponent,
    HelpArticlesDirective,
    ButtonComponent,
    TutorialDialogNavComponent,
    PushPipe,
  ],
})
export class TutorialDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<null, null>
{
  private readonly dialog = inject(DialogRef);
  private readonly data = inject<{
    config: TutorialStep[];
    analyticsContext: string;
  }>(DialogData);
  private readonly themeService = inject(PlatformThemeService);
  private readonly i18n = inject(I18nService);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly cdRef: ChangeDetectorRef;
  public readonly analytics = inject(Analytics);
  public readonly translations = inject<TutorialDialogModuleTranslations>(TutorialTranslations);

  public dialogBodyTranslations: any;
  public setDialogData: (data: null, triggerUpdate: boolean) => void;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      activeStepIndex: 0,
      steps: this.data.config,
    });

    const { dialogBody } = this.translations;

    this.dialogBodyTranslations = {
      continue: this.i18n.translate(dialogBody.continue),
      finish: this.i18n.translate(dialogBody.finish),
      learnMore: this.i18n.translate(dialogBody.learnMore),
      skip: this.i18n.translate(dialogBody.skip),
      startTour: dialogBody.startTour ? this.i18n.translate(dialogBody.startTour) : '',
    };
  }

  public get stepsCount(): number {
    return this.state.steps && this.state.steps.length;
  }

  public get activeStep(): TutorialStep {
    return this.state.steps[this.state.activeStepIndex];
  }

  public get isLast(): boolean {
    return this.state.activeStepIndex === this.state.steps.length - 1;
  }

  public get imgUrl() {
    return (
      this.themeService.isHighDPI
        ? this.activeStep.imgUrl2x || this.activeStep.imgUrl
        : this.activeStep.imgUrl
    )!;
  }

  public get youtubeUrl(): SafeResourceUrl | boolean {
    const url = `https://www.youtube.com/embed/${this.activeStep.youtubeVideoId}`;

    return (
      !!this.activeStep.youtubeVideoId &&
      // eslint-disable-next-line ban/ban
      this.sanitizer.bypassSecurityTrustResourceUrl(url)
    );
  }

  public setStep(index: number): void {
    this.setState({ ...this.state, activeStepIndex: index });
  }

  public nextStep(): void {
    this.setState({ ...this.state, activeStepIndex: this.state.activeStepIndex + 1 });
  }

  public close(): void {
    this.dialog.close();
  }

  public trackFinishButton(): void {
    if (this.data.analyticsContext)
      this.analytics.track(`${this.data.analyticsContext} Tutorial_FinishButtonClick`);
  }

  public trackSkipButton(): void {
    if (this.data.analyticsContext)
      this.analytics.track(`${this.data.analyticsContext} Tutorial_SkipButtonClick`, {
        step: this.state.activeStepIndex + 1,
      });
  }
}
